import { Deal } from '@paintscout/api'
import { useUser } from '@paintscout/ui-business'
import { isUserAssigned, isUserOwner } from '@paintscout/util/builder'
import { useMemo } from 'react'
import * as Sentry from '@sentry/react'

/**
 * Computes edit/view/delete permissions for a single deal.
 *
 * A deal is accessible to a user if any of the following is true:
 *   - They are the deal owner
 *   - They are assigned to the deal (assignedUsers on the deal includes
 *     owners and assignees of linked quotes/invoices, synced on quote save)
 */
export const useDealPermissions = (deal: Deal | null) => {
  const { hasPermissions, user } = useUser()

  const isOwner = deal ? isUserOwner(deal as any, user?.user_id) : false
  const isAssigned = deal ? isUserAssigned(deal as any, user?.user_id) : false
  const isLinkedUser = isOwner || isAssigned

  const permissionToEditAssignedDeals = hasPermissions(['write:assigned-deals'])
  const permissionToEditAllDeals = hasPermissions(['write:all-deals'])

  // this should always be true if a user has access to the EditDeal, however we are noticing times where it flakes.
  // hardcoding to true and logging details to sentry when it is false to try to track down the issue
  const _permissionToEdit = permissionToEditAllDeals || (permissionToEditAssignedDeals && isLinkedUser)

  if (!_permissionToEdit) {
    Sentry.captureMessage('User does not have permission to edit deal', {
      level: 'warning',
      extra: {
        userId: user?.user_id,
        dealId: deal?._id,
        isOwner,
        isAssigned,
        isLinkedUser,
        permissionToEditAssignedDeals,
        permissionToEditAllDeals,
        finalPermissionToEdit: _permissionToEdit
      }
    })
  }

  const permissionToEdit = true

  return useMemo(
    () => ({ permissionToEdit }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deal, user, hasPermissions]
  )
}
